import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, RefreshCw, Mail, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { start2FA, verify2FA, start2FASms, verify2FASms } from "@admin/services/adminApi";
import { ADMIN_BASE } from "@admin/constants/route";
import { toast } from "sonner";

/**
 * Admin 2FA verification screen.
 *
 * UX:
 *   - On mount, requests an OTP via POST /admin/auth/2fa/start (skips if a
 *     fresh code was sent in the last 60s, surfaces a friendly cooldown).
 *   - Renders a 6-digit pin input, "Resend code" link, and a verify button.
 *   - On success, refreshes /auth/me so the AuthContext picks up the
 *     `two_factor_verified_at` stamp, then redirects back to the route the
 *     user was originally targeting.
 *
 * Security:
 *   - Code sent via email (Resend) — never displayed in UI.
 *   - Backend rate-limits both resends (60s cooldown, 5/hour) and attempts (5).
 */
const AUTO_SEND_GUARD_MS = 60 * 1000;
const autoKey = (uid, stage = "email") => `advoset.admin2fa.auto.${stage}.${uid}`;

export default function Admin2FA() {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || ADMIN_BASE;
  // Step-up: a sensitive action found the session's 2FA stamp older than the
  // freshness window. The session itself is fine — re-verify and go back.
  const stepUp = !!location.state?.stepUp;

  const [code, setCode] = useState("");
  // "email" → (Super Admin only) "sms". Resumes at SMS if the e-mail factor is already on the session.
  const [stage, setStage] = useState(() => (user?.sms_factor_required && user?.email_factor_verified_at && !user?.two_factor_satisfied) ? "sms" : "email");
  const [phoneHint, setPhoneHint] = useState(user?.phone ? `${user.phone.slice(0, 3)}••••${user.phone.slice(-4)}` : null);
  useEffect(() => {
    if (user?.sms_factor_required && user?.email_factor_verified_at && !user?.two_factor_satisfied) setStage("sms");
  }, [user?.sms_factor_required, user?.email_factor_verified_at, user?.two_factor_satisfied]);
  const [busy, setBusy] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState("");

  // Inject noindex
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) { meta = document.createElement("meta"); meta.setAttribute("name", "robots"); document.head.appendChild(meta); }
    const prev = meta.getAttribute("content");
    meta.setAttribute("content", "noindex,nofollow,noarchive,nosnippet");
    return () => { if (prev != null) meta.setAttribute("content", prev); };
  }, []);

  // If 2FA already satisfied or user isn't an admin, leave immediately.
  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") {
      navigate("/", { replace: true });
      return;
    }
    if (user.two_factor_satisfied && !stepUp) {
      navigate(from, { replace: true });
    }
  }, [user, loading, navigate, from, stepUp]);

  // Mask the email for the UI (never show full string)
  useEffect(() => {
    if (!user?.email) return;
    const [name, dom] = user.email.split("@");
    if (!dom) return;
    const masked = (name || "").slice(0, 2) + "•".repeat(Math.max(name.length - 2, 1));
    setMaskedEmail(`${masked}@${dom}`);
  }, [user]);

  // Auto-request a code ONCE per 2FA challenge. The guard lives in
  // sessionStorage (keyed by user) so StrictMode remounts, user-object
  // refreshes and back-navigation don't re-fire it; explicit Resend bypasses
  // it. The backend cooldown remains the real authority (other tabs/workers).
  useEffect(() => {
    if (!user || user.role !== "admin" || (user.two_factor_satisfied && !stepUp)) return;
    const key = autoKey(user.user_id, stage);
    const last = Number(sessionStorage.getItem(key) || 0);
    const elapsed = Date.now() - last;
    if (elapsed < AUTO_SEND_GUARD_MS) {
      setCooldown(Math.ceil((AUTO_SEND_GUARD_MS - elapsed) / 1000));
      return;
    }
    sessionStorage.setItem(key, String(Date.now()));
    requestCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, stage]);

  // Cooldown ticker
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestCode = async () => {
    setError("");
    setBusy(true);
    try {
      const r = stage === "sms" ? await start2FASms() : await start2FA();
      if (user?.user_id) sessionStorage.setItem(autoKey(user.user_id, stage), String(Date.now()));
      if (stage === "sms" && r.phone_hint) setPhoneHint(r.phone_hint);
      toast.success(stage === "sms" ? `Code texted to ${r.phone_hint || "your phone"}` : `Code sent to ${r.email}`);
      setCooldown(60);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const detail = (d && d.message) || d || "Could not send code";
      setError(detail);
      // Server returns "Please wait Ns before requesting another code." — pull seconds
      const m = String(detail).match(/(\d+)s/);
      if (m) setCooldown(Number(m[1]));
    }
    setBusy(false);
  };

  const onVerify = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) {
      setError(stage === "sms" ? "Enter the 6-digit code from the SMS." : "Enter the 6-digit code from your email.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      if (stage === "sms") {
        await verify2FASms(code);
      } else {
        const r = await verify2FA(code);
        if (r && r.complete === false) {
          // Super Admin: e-mail factor accepted → SMS factor next.
          if (r.phone_hint) setPhoneHint(r.phone_hint);
          setCode("");
          setStage("sms");
          setCooldown(0);
          setBusy(false);
          toast.success("E-mail code verified — now enter the SMS code");
          return;
        }
      }
      if (user?.user_id) { sessionStorage.removeItem(autoKey(user.user_id, "email")); sessionStorage.removeItem(autoKey(user.user_id, "sms")); }
      toast.success(stepUp ? "Verified — please retry the action" : "Verified — welcome back");
      await checkAuth();
      navigate(from, { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError((d && d.message) || d || "Verification failed");
      setCode("");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#0A2540] grid place-items-center p-6">
      <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl" data-testid="admin-2fa-card" data-stage={stage}>
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-[#D4AF37] grid place-items-center text-[#0A2540]">
            <ShieldCheck size={22} />
          </div>
          <div>
            <p className="text-xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>{stepUp ? "Confirm it's you" : "Two-factor verification"}</p>
            <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold" data-testid="admin-2fa-mode">{stepUp ? "Fresh code needed for this action" : "Mandatory for your role"}</p>
          </div>
        </div>

        {stepUp && (
          <p className="mb-3 text-xs text-slate-600 bg-[#FBF4DF] border border-[#F1E3B5] rounded-xl px-3 py-2" data-testid="admin-2fa-stepup-note">
            You're still signed in. Sensitive actions (listing, verification overrides, payouts) need a code verified within the last 30 minutes — enter a fresh one and you'll be taken back to where you were.
          </p>
        )}
        <p className="text-sm text-slate-600 inline-flex items-start gap-2">
          <Mail size={14} className="mt-0.5 text-slate-400" />
          {stage === "sms"
            ? <>Step 2 of 2 — we texted a 6-digit code to <span className="font-semibold text-[#0A2540]" data-testid="admin-2fa-phone">{phoneHint || "your verified phone"}</span>.</>
            : <>{user?.sms_factor_required ? "Step 1 of 2 — " : ""}We sent a 6-digit code to <span className="font-semibold text-[#0A2540]" data-testid="admin-2fa-email">{maskedEmail}</span>.</>}
          Code expires in 5 minutes.
        </p>

        <form onSubmit={onVerify} className="mt-6">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            placeholder="000000"
            maxLength={6}
            autoFocus
            data-testid="admin-2fa-code-input"
            className="w-full text-center text-3xl tracking-[0.6em] font-extrabold text-[#0A2540] py-4 border-2 border-slate-200 rounded-xl outline-none focus:border-[#0A2540] transition"
            style={{ fontFamily: "Poppins" }}
          />
          {error && (
            <p className="mt-3 text-xs text-rose-700 inline-flex items-center gap-1" data-testid="admin-2fa-error">
              <AlertCircle size={12} /> {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={busy || code.length !== 6}
            data-testid="admin-2fa-verify-btn"
            className="mt-4 w-full rounded-xl adv-btn-primary font-bold h-11"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
            Verify and continue
          </Button>
        </form>

        <button
          onClick={requestCode}
          disabled={busy || cooldown > 0}
          data-testid="admin-2fa-resend-btn"
          className="mt-4 text-xs font-bold text-[#0A2540] hover:underline disabled:opacity-40 inline-flex items-center gap-1"
        >
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} />
          {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
        </button>

        <div className="mt-5 flex items-center justify-center gap-4 text-[11px] text-slate-400">
          <span>Codes expire in 5 minutes</span>
          <span>·</span>
          <span>Max 5 attempts</span>
        </div>
      </div>
    </div>
  );
}
